import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Building2,
  Users,
  IndianRupee,
  Calendar,
  TrendingUp,
  Clock,
  CheckCircle2,
  Eye,
  Download,
  Loader2,
  Award,
  Edit2,
  Save
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from '../../hooks/use-toast';

import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { clientsAPI, usersAPI, plansAPI, incentivesAPI } from '../../services/api';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { usePrivacy } from '../../contexts/PrivacyContext';

const ClientsPage = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { formatMoney } = usePrivacy();
  const formatCurrency = formatMoney; // Alias for compatibility
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [myEarnings, setMyEarnings] = useState(0);
  const [stats, setStats] = useState({
    total_clients: 0,
    total_mrr: 0,
    my_clients: 0,
    my_earned: 0,
    total_users: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const [viewingClient, setViewingClient] = useState(null);
  const [planFilter, setPlanFilter] = useState('all');
  const [editingPlanAmount, setEditingPlanAmount] = useState(false);
  const [newPlanAmount, setNewPlanAmount] = useState(0);
  const [updatingPlan, setUpdatingPlan] = useState(false);
  
  const isAdminOrManager = currentUser?.role === 'ADMIN' || currentUser?.role === 'MANAGER';

  // Fetch data on mount using clientsAPI
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [clientsRes, usersRes, plansRes, incentivesRes] = await Promise.all([
          clientsAPI.list(),
          usersAPI.list(),
          plansAPI.list(),
          incentivesAPI.listRecords({ page_size: 500 }).catch(() => ({ data: [] }))
        ]);
        
        // Get clients data
        const clientsData = clientsRes.data?.clients || [];
        setClients(clientsData);
        setUsers(usersRes.data || []);
        setPlans(plansRes.data || []);
        
        // Calculate my earnings from incentives
        const incentivesData = Array.isArray(incentivesRes.data) ? incentivesRes.data : [];
        const totalEarned = incentivesData.reduce((sum, inc) => sum + (inc.amount_earned || 0), 0);
        setMyEarnings(totalEarned);
        
        // Calculate stats - show user's own clients for agents
        const myClients = clientsData.filter(c => c.account_owner_id === currentUser?.id || c.closed_by_user_id === currentUser?.id);
        
        setStats({
          total_clients: isAdminOrManager ? clientsData.length : myClients.length,
          total_mrr: isAdminOrManager 
            ? clientsData.reduce((sum, c) => sum + (c.mrr || 0), 0)
            : myClients.reduce((sum, c) => sum + (c.mrr || 0), 0),
          my_clients: myClients.length,
          my_earned: totalEarned,
          total_users: isAdminOrManager 
            ? clientsData.reduce((sum, c) => sum + (c.total_users || 0), 0)
            : myClients.reduce((sum, c) => sum + (c.total_users || 0), 0)
        });
      } catch (error) {
        console.error('Failed to fetch clients data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentUser, isAdminOrManager]);

  const getEmployeeName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unknown';
  };

  const getPlanName = (planId) => {
    const plan = plans.find(p => p.id === planId);
    return plan?.plan_name || 'Standard';
  };

  const toggleClientSelection = (id) => {
    setSelectedClients(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleAllClients = () => {
    if (selectedClients.length === clients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(clients.map(c => c.id));
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch =
      client.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.contact_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const clientPlan = getPlanName(client.plan_id);
    const matchesPlan = planFilter === 'all' || clientPlan === planFilter;
    return matchesSearch && matchesPlan;
  });

  const handleEditPlanAmount = (client) => {
    setNewPlanAmount(client.plan_amount || client.mrr || 0);
    setEditingPlanAmount(true);
  };

  const handleSavePlanAmount = async () => {
    if (!viewingClient) return;
    try {
      setUpdatingPlan(true);
      await clientsAPI.updatePlanAmount(viewingClient.id, newPlanAmount);
      // Refresh client data
      setViewingClient({ ...viewingClient, plan_amount: newPlanAmount, mrr: newPlanAmount });
      setClients(clients.map(c => c.id === viewingClient.id ? { ...c, plan_amount: newPlanAmount, mrr: newPlanAmount } : c));
      setEditingPlanAmount(false);
      toast({ title: 'Plan amount updated successfully' });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to update plan amount',
        description: error.response?.data?.detail || error.message
      });
    } finally {
      setUpdatingPlan(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading clients...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header - Mobile Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100">Clients</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Converted clients with complete history</p>
        </div>
        <Button variant="outline" size="sm" className="w-full sm:w-auto">
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
      </div>

      {/* Stats - Mobile Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-2 sm:p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-sm text-slate-500 dark:text-slate-400">
                  {isAdminOrManager ? 'Total' : 'My'} Clients
                </p>
                <p className="text-lg sm:text-2xl font-bold text-slate-800 dark:text-slate-100">{stats.total_clients}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-2 sm:p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <IndianRupee className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-sm text-slate-500 dark:text-slate-400">MRR</p>
                <p className="text-base sm:text-xl font-bold text-slate-800 dark:text-slate-100 truncate">{formatCurrency(stats.total_mrr)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-2 sm:p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <Award className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-sm text-slate-500 dark:text-slate-400">Earned</p>
                <p className="text-base sm:text-xl font-bold text-slate-800 dark:text-slate-100 truncate">{formatCurrency(stats.my_earned)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-2 sm:p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-sm text-slate-500 dark:text-slate-400">Users</p>
                <p className="text-lg sm:text-2xl font-bold text-slate-800 dark:text-slate-100">{stats.total_users}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search - Mobile Responsive */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white dark:bg-slate-800 w-full"
          />
        </div>
        <Select value={planFilter} onValueChange={setPlanFilter}>
          <SelectTrigger className="w-[180px] bg-white dark:bg-slate-800">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Plans" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Plans</SelectItem>
            {plans.map(plan => (
              <SelectItem key={plan.id} value={plan.plan_name}>{plan.plan_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-3">
        {filteredClients.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p>{clients.length === 0 ? 'No clients yet. Convert deals to see them here.' : 'No clients match your search.'}</p>
          </div>
        ) : (
          filteredClients.map((client) => (
            <Card
              key={client.id}
              className={cn(
                'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 cursor-pointer active:bg-slate-50 dark:active:bg-slate-700',
                selectedClients.includes(client.id) && 'ring-2 ring-blue-200 dark:ring-blue-900',
                client.is_terminated && 'bg-red-50 dark:bg-red-900/10'
              )}
              onClick={() => navigate(`/clients/${client.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className={cn('font-semibold text-slate-900 dark:text-slate-100 truncate', client.is_terminated && 'text-red-700 dark:text-red-300')}>
                          {client.company_name}
                        </h3>
                        <p className="text-sm text-slate-500 truncate">{client.contact_name || '-'}</p>
                      </div>

                      {/* Checkbox: top-right (option 2) */}
                      <button
                        type="button"
                        className="p-2 -m-1 rounded active:bg-slate-100 dark:active:bg-slate-700 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleClientSelection(client.id);
                        }}
                        aria-label={selectedClients.includes(client.id) ? 'Unselect client' : 'Select client'}
                      >
                        <Checkbox checked={selectedClients.includes(client.id)} className="h-5 w-5" />
                      </button>
                    </div>

                    {client.is_terminated && (
                      <div className="mt-2">
                        <Badge className="bg-red-100 text-red-700 text-xs">Terminated</Badge>
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 w-8 p-0 flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/clients/${client.id}`);
                    }}
                    title="View"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                    <Badge variant="outline" className="dark:border-slate-600">
                      {getPlanName(client.plan_id)}
                    </Badge>
                  </div>
                  <div className={cn('text-right font-semibold', client.is_terminated ? 'text-red-500 line-through' : 'text-emerald-600 dark:text-emerald-400')}>
                    {formatCurrency(client.mrr || 0)}/mo
                  </div>

                  <div className="flex items-center gap-1.5 text-slate-500 col-span-2">
                    <Calendar className="w-3.5 h-3.5" />
                    <span className="truncate">Onboarded: {client.onboarded_date || client.created_at?.split('T')[0] || '-'}</span>
                  </div>

                  <div className="flex items-center gap-1.5 text-slate-500 col-span-2">
                    <Users className="w-3.5 h-3.5" />
                    <span className="truncate">Employee: {getEmployeeName(client.primary_employee_id)}</span>
                  </div>

                  <div className="flex items-center justify-between col-span-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                    <div className="text-xs text-slate-500">
                      <span className="font-medium text-emerald-600">{formatCurrency(client.company_earned || 0)}</span>
                      <span className="text-slate-400"> earned</span>
                    </div>
                    <div className="text-xs text-slate-500">
                      <span className="font-medium text-amber-600">{formatCurrency(client.company_to_earn || 0)}</span>
                      <span className="text-slate-400"> to earn</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <Card className="hidden lg:block bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-900">
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={selectedClients.length === clients.length && clients.length > 0}
                    onCheckedChange={toggleAllClients}
                  />
                </TableHead>
                <TableHead className="font-semibold">Company</TableHead>
                <TableHead className="font-semibold">Plan</TableHead>
                <TableHead className="font-semibold">MRR</TableHead>
                <TableHead className="font-semibold">Onboarded</TableHead>
                <TableHead className="font-semibold">Employee</TableHead>
                <TableHead className="font-semibold">Company Earned</TableHead>
                <TableHead className="font-semibold">To Earn</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                    {clients.length === 0 ? 'No clients yet. Convert deals to see them here.' : 'No clients match your search.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredClients.map((client) => (
                  <TableRow
                    key={client.id}
                    className={cn(
                      'hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer',
                      selectedClients.includes(client.id) && 'bg-blue-50 dark:bg-blue-900/20',
                      client.is_terminated && 'bg-red-50 dark:bg-red-900/10'
                    )}
                    onClick={() => navigate(`/clients/${client.id}`)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedClients.includes(client.id)}
                        onCheckedChange={() => toggleClientSelection(client.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div>
                          <p className={cn("font-medium dark:text-slate-100", client.is_terminated && "text-red-600 dark:text-red-400")}>{client.company_name}</p>
                          <p className="text-xs text-slate-500">{client.contact_name}</p>
                        </div>
                        {client.is_terminated && (
                          <Badge className="bg-red-100 text-red-700 text-xs">Terminated</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="dark:border-slate-600">{getPlanName(client.plan_id)}</Badge>
                    </TableCell>
                    <TableCell className={cn("font-semibold", client.is_terminated ? "text-red-500 line-through" : "text-emerald-600 dark:text-emerald-400")}>
                      {formatCurrency(client.mrr || 0)}/mo
                    </TableCell>
                    <TableCell className="dark:text-slate-300">{client.onboarded_date || client.created_at?.split('T')[0] || '-'}</TableCell>
                    <TableCell className="dark:text-slate-300">{getEmployeeName(client.primary_employee_id)}</TableCell>
                    <TableCell className="font-semibold text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(client.company_earned || 0)}
                    </TableCell>
                    <TableCell className="font-semibold text-amber-600 dark:text-amber-400">
                      {formatCurrency(client.company_to_earn || 0)}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm"><Eye className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Client Profile Modal */}
      <Dialog open={!!viewingClient} onOpenChange={() => setViewingClient(null)}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewingClient?.company_name}</DialogTitle>
            <DialogDescription>Complete client profile and history</DialogDescription>
          </DialogHeader>
          {viewingClient && (
            <Tabs defaultValue="overview" className="space-y-4">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="financials">Financials</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4">
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <div>
                    <Label className="text-slate-500">Contact Name</Label>
                    <p className="font-medium dark:text-slate-100">{viewingClient.contact_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Phone</Label>
                    <p className="font-medium dark:text-slate-100">{viewingClient.contact_phone || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Email</Label>
                    <p className="font-medium dark:text-slate-100">{viewingClient.contact_email || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Plan</Label>
                    <Badge>{getPlanName(viewingClient.plan_id)}</Badge>
                  </div>
                  <div>
                    <Label className="text-slate-500">Total Users</Label>
                    <p className="font-medium dark:text-slate-100">{viewingClient.total_users || 0}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Onboarded</Label>
                    <p className="font-medium dark:text-slate-100">{viewingClient.onboarded_date || viewingClient.created_at?.split('T')[0] || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Card className="dark:bg-slate-900">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-slate-500">Primary Employee</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="font-medium dark:text-slate-100">{getEmployeeName(viewingClient.primary_employee_id)}</p>
                      <p className="text-sm text-emerald-600">Earned: {formatCurrency(viewingClient.employee_earned || 0)}</p>
                    </CardContent>
                  </Card>
                  <Card className="dark:bg-slate-900">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-slate-500">Sales Manager</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="font-medium dark:text-slate-100">{getEmployeeName(viewingClient.sales_manager_id)}</p>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="financials" className="space-y-4">
                {isAdminOrManager && (
                  <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center justify-between">
                        <span className="text-blue-700 dark:text-blue-400">Update Plan Amount</span>
                        {!editingPlanAmount ? (
                          <Button size="sm" variant="outline" onClick={() => handleEditPlanAmount(viewingClient)}>
                            <Edit2 className="w-4 h-4 mr-1" /> Edit
                          </Button>
                        ) : (
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => setEditingPlanAmount(false)} disabled={updatingPlan}>
                              Cancel
                            </Button>
                            <Button size="sm" onClick={handleSavePlanAmount} disabled={updatingPlan}>
                              {updatingPlan ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                              Save
                            </Button>
                          </div>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {editingPlanAmount ? (
                        <div className="flex items-center gap-2">
                          <Label>Monthly Plan Amount (₹):</Label>
                          <Input
                            type="number"
                            value={newPlanAmount}
                            onChange={(e) => setNewPlanAmount(parseFloat(e.target.value) || 0)}
                            className="w-40"
                          />
                        </div>
                      ) : (
                        <p className="text-lg font-semibold text-blue-700 dark:text-blue-400">
                          Current: {formatCurrency(viewingClient?.plan_amount || viewingClient?.mrr || 0)}/month
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <Card className="dark:bg-slate-900">
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-500">Deal Value</p>
                      <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{formatCurrency(viewingClient.deal_value || 0)}</p>
                    </CardContent>
                  </Card>
                  <Card className="dark:bg-slate-900">
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-500">MRR</p>
                      <p className="text-2xl font-bold text-emerald-600">{formatCurrency(viewingClient.mrr || 0)}/mo</p>
                    </CardContent>
                  </Card>
                  <Card className="dark:bg-slate-900">
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-500">One-Time Charge</p>
                      <p className="text-2xl font-bold text-purple-600">{formatCurrency(viewingClient.one_time_charge || 0)}</p>
                    </CardContent>
                  </Card>
                  <Card className="dark:bg-slate-900">
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-500">Employee Incentive Earned</p>
                      <p className="text-2xl font-bold text-blue-600">{formatCurrency(viewingClient.employee_earned || 0)}</p>
                    </CardContent>
                  </Card>
                </div>

                <Card className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800">
                  <CardHeader>
                    <CardTitle className="text-emerald-700 dark:text-emerald-400">Company Revenue</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-emerald-600">Already Earned</p>
                        <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-400">{formatCurrency(viewingClient.company_earned || 0)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-amber-600">To Be Earned</p>
                        <p className="text-3xl font-bold text-amber-600">{formatCurrency(viewingClient.company_to_earn || 0)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingClient(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsPage;
